import { SiteDTO } from "api";

/**
 * Toggles values in a selection array, ensuring uniqueness.
 * - If `unchecked` is true, removes the given value from the selection.
 * - If the resulting selection includes `"ALL"`, resets to `null`.
 * - Returns `null` if no items remain selected.
 *
 * @param {Array<{ value: string | number }>} arr - Current selection array of objects with a `value` field.
 * @param {string | number} value - The value to toggle in the selection.
 * @param {boolean} [unchecked] - Whether the value should be removed instead of added.
 * @returns {Array<string | number> | null} Updated selection values or `null` if reset/empty.
 */
export const toggleSelectionValues = (
  arr: any[],
  value: string | number,
  unchecked?: boolean,
) => {
  const currentSelection = Array.from(
    new Set([...arr.map((item: any) => item.value), value]),
  );
  if (unchecked) {
    const filteredSelection = arr?.filter((item: any) => item.value !== value)?.map((item: any) => item.value);
    if (!filteredSelection.length) return null;
    return filteredSelection;
  }
  return currentSelection.includes("ALL") ? null : currentSelection;
};

/**
 * Filters a list of sites to include only those within the specified country regions.
 * Transforms the resulting sites into objects with `name`, `label`, and `value` properties.
 *
 * @param {SiteDTO[]} sitesData - Full list of site data.
 * @param {Array<{ value: string[] | string }>} currentCountryRegions - List of selected country regions.
 * @returns {Array<{ name: string; label: string; value: string | number }>} Filtered and formatted site options.
 */
export const getSitesFiltered = (sitesData: SiteDTO[], currentCountryRegions: any[]) =>
  sitesData?.filter((site: SiteDTO) =>
    currentCountryRegions.map((region: any) => region.value).flat().includes(site.countryRegion))
    .map((site: SiteDTO) => ({
      name: site.name,
      label: site.name,
      value: site.id,
    })) ?? [];
